import {Alert, Button, DataStatus, openConfirmDialog, showNotification} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import type {Money} from '@dms/api/shared';
import {useDeleteSaleVehicleLeasingMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {PendingChangesBar} from '../../../../../../components/PendingChangesBar/PendingChangesBar';
import {useLeasingOverview} from './hooks/useLeasingOverview';
import {useVehicleLeasingDraft} from './hooks/useVehicleLeasingDraft';
import {LeasingCellEditorDialog} from './LeasingCellEditorDialog';
import {LeasingCommonParameters} from './LeasingCommonParameters';
import {LeasingConfiguration} from './LeasingConfiguration';
import {LeasingEmptyState} from './LeasingEmptyState';
import {LeasingRateMatrixSection} from './LeasingRateMatrixSection';
import {LeasingCellCoordinates} from './types/LeasingCellCoordinates';
import {PendingEditorChangeHandler} from './types/PendingEditor';
import {applyDefaultLeasingListPrice} from './utils/leasingBody/applyDefaultLeasingListPrice';
import {createEmptyLeasingBody} from './utils/leasingBody/createEmptyLeasingBody';
import {resolveLeasingCurrency} from './utils/leasingBody/resolveLeasingCurrency';

interface LeasingPanelProps {
  saleVehicleId: string;
  currency: string;
  vehiclePrice?: Money | null;
  isCreateAllowed: boolean;
  isUpdateAllowed: boolean;
  isDeleteAllowed: boolean;
  onPendingChange?: PendingEditorChangeHandler;
  onSavingChange?: (isSaving: boolean) => void;
  onValidationError?: () => void;
}

export function LeasingPanel(props: LeasingPanelProps) {
  const [editedCellCoordinates, setEditedCellCoordinates] = useState<LeasingCellCoordinates | null>(
    null
  );

  const overview = useLeasingOverview({
    saleVehicleId: props.saleVehicleId,
    vehiclePrice: props.vehiclePrice,
    isCreateAllowed: props.isCreateAllowed,
    isUpdateAllowed: props.isUpdateAllowed,
  });
  const leasingDraft = useVehicleLeasingDraft({
    saleVehicleId: props.saleVehicleId,
    baselineBody: overview.baselineBody,
    isWriteAllowed: overview.isWriteAllowed,
    onPendingChange: props.onPendingChange,
    onSavingChange: props.onSavingChange,
    onValidationError: props.onValidationError,
  });
  const [deleteLeasing, deleteStatus] = useDeleteSaleVehicleLeasingMutation();

  const vehicleLeasingBody = leasingDraft.draft ?? overview.baselineBody;
  const displayedVehicleLeasingBody = vehicleLeasingBody ?? createEmptyLeasingBody();
  const leasingCurrency = resolveLeasingCurrency(vehicleLeasingBody, props.currency);
  const isEditingAllowed = overview.isWriteAllowed && !leasingDraft.isSaving;

  const handleConfigureLeasing = () =>
    leasingDraft.setDraft(
      applyDefaultLeasingListPrice(createEmptyLeasingBody(), props.vehiclePrice)
    );

  const handleDelete = () => {
    if (!props.isDeleteAllowed) {
      return;
    }

    openConfirmDialog({
      onConfirm: () =>
        deleteLeasing({saleVehicleId: props.saleVehicleId})
          .unwrap()
          .then(() => {
            leasingDraft.discardDraft();
            showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
          })
          .catch(handleApiError),
    });
  };

  return (
    <>
      <DataStatus isLoading={overview.isLoading} isError={overview.isError}>
        <Show when={isNil(vehicleLeasingBody)}>
          <LeasingEmptyState
            isCreateAllowed={props.isCreateAllowed}
            onConfigureLeasing={handleConfigureLeasing}
          />
        </Show>

        <Show when={vehicleLeasingBody}>
          <VStack spacing={4}>
            <Show when={overview.state === 'EXPIRED'}>
              <Alert
                variant="warning"
                title={i18n.t('entity.vehicleFinancingTemplate.status.expired')}
                message={i18n.t('page.vehicle.financingAndLeasing.expiredLeasingExplanation')}
              />
            </Show>

            <LeasingConfiguration
              vehicleLeasingBody={displayedVehicleLeasingBody}
              financialInstitutionError={leasingDraft.financialInstitutionError}
              isReadOnly={!isEditingAllowed}
              onChange={leasingDraft.setDraft}
            />

            <LeasingRateMatrixSection
              vehicleLeasingBody={displayedVehicleLeasingBody}
              currency={leasingCurrency}
              isReadOnly={!isEditingAllowed}
              hasActiveVariantError={leasingDraft.isActiveSelectionErrorVisible}
              onEditCell={setEditedCellCoordinates}
            />

            <LeasingCommonParameters
              vehicleLeasingBody={displayedVehicleLeasingBody}
              currency={leasingCurrency}
              isReadOnly={!isEditingAllowed}
              onChange={leasingDraft.setDraft}
            />

            <Show when={overview.isPersisted && props.isDeleteAllowed}>
              <HStack justify="flex-end">
                <Button
                  title={i18n.t('page.vehicle.financingAndLeasing.clearLeasing')}
                  variant="secondary"
                  leftIcon="action/delete"
                  isLoading={deleteStatus.isLoading}
                  onClick={handleDelete}
                />
              </HStack>
            </Show>

            <Show when={leasingDraft.isDirty}>
              <PendingChangesBar
                title={i18n.t('general.notifications.unsavedChanges.title')}
                isSaving={leasingDraft.isSaving}
                onDiscard={leasingDraft.discardDraft}
                onSave={leasingDraft.saveDraft}
              />
            </Show>
          </VStack>
        </Show>
      </DataStatus>

      <Show when={isNotNil(editedCellCoordinates) && isNotNil(vehicleLeasingBody)}>
        <LeasingCellEditorDialog
          cellCoordinates={editedCellCoordinates}
          vehicleLeasingBody={vehicleLeasingBody}
          currency={leasingCurrency}
          isReadOnly={!isEditingAllowed}
          onChange={leasingDraft.setDraft}
          onClose={() => setEditedCellCoordinates(null)}
        />
      </Show>
    </>
  );
}
